import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UtilitesService } from "app/base-module/base-service/utilites.service";
import { ApiPaginationResponseDataNonNullable, ApiResponse } from "app/shared/models/api-response.model";
import { Observable } from "rxjs";
import {
    BoletaDeIntermediacao,
    BoletaDeIntermediacaoQueryParams,
    BookBoletaPayload,
    CreateBoletaPayload,
    EditBoletaPayload,
    LogEmailBoletaPayload,
} from "../models/boleta-de-intermediacao.model";

@Injectable({ providedIn: "root" })
export class BoletaDeIntermediacaoService {
    private baseUrl = "rendafixa/boletas-de-intermediacao";

    constructor(private _http: HttpClient, public utilitesService: UtilitesService) {}

    /**
     * Busca as boletas de intermediação com base nos filtros fornecidos.
     * @param filtros Filtros para buscar as boletas
     * @returns Um Observable com a lista de boletas encontradas
     */
    buscarBoletas(
        filtros: BoletaDeIntermediacaoQueryParams
    ): Observable<ApiPaginationResponseDataNonNullable<BoletaDeIntermediacao>> {
        return this._http.get<ApiPaginationResponseDataNonNullable<BoletaDeIntermediacao>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}`,
            {
                params: filtros as unknown as Record<string, string>,
            }
        );
    }

    /**
     * Busca uma boleta de intermediação por ID
     * @param id ID da boleta de intermediação
     * @returns Observable com a boleta de intermediação encontrada
     */
    getBoletaById(id: number): Observable<ApiResponse<BoletaDeIntermediacao>> {
        return this._http.get<ApiResponse<BoletaDeIntermediacao>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}`
        );
    }

    /**
     * Cria uma nova boleta de intermediação
     * @param payload Dados da boleta de intermediação
     * @returns Observable com o resultado da criação
     */
    createBoleta(payload: CreateBoletaPayload): Observable<ApiResponse<BoletaDeIntermediacao>> {
        return this._http.post<ApiResponse<BoletaDeIntermediacao>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}`,
            payload
        );
    }

    /**
     * Edita uma boleta de intermediação
     * @param id ID da boleta de intermediação
     * @param payload Dados da boleta de intermediação para edição
     * @returns Observable com o resultado da edição
     */
    editBoleta(id: number, payload: EditBoletaPayload): Observable<ApiResponse<BoletaDeIntermediacao>> {
        return this._http.patch<ApiResponse<BoletaDeIntermediacao>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}`,
            payload
        );
    }

    /**
     * Reserva uma boleta de intermediação
     * @param id ID da boleta de intermediação
     * @param payload Dados da reserva da boleta de intermediação
     * @returns Observable com o resultado da reserva
     */
    bookBoleta(id: number, payload: BookBoletaPayload): Observable<ApiResponse<BoletaDeIntermediacao>> {
        return this._http.post<ApiResponse<BoletaDeIntermediacao>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}/reservar`,
            payload
        );
    }

    /**
     * Exclui uma boleta de intermediação
     * @param id ID da boleta de intermediação
     * @returns Observable com o resultado da exclusão
     */
    deleteBoleta(id: number): Observable<any> {
        return this._http.delete<void>(`${this.utilitesService.urlPrefix}${this.baseUrl}/${id}`);
    }

    /**
     * Exporta as boletas de intermediação
     * @param params Filtros para exportação
     * @returns Observable com o resultado da exportação
     */
    exportarBoletas(params: BoletaDeIntermediacaoQueryParams): Observable<Blob> {
        return this._http.get(`${this.utilitesService.urlPrefix}${this.baseUrl}/export`, {
            params: params as unknown as Record<string, string>,
            responseType: "blob",
            headers: new HttpHeaders({
                Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
        });
    }

    /**
     * Revalida uma boleta de intermediação
     * @param id ID da boleta de intermediação
     * @returns Observable com o resultado da revalidação
     */
    revalidarBoleta(id: number): Observable<any> {
        return this._http.post<any>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}/revalidar`,
            {},
            { responseType: "text" as "json" }
        );
    }

    /**
     * Marca uma boleta de intermediação como não revalidada
     * @param id ID da boleta de intermediação
     * @returns Observable com o resultado da operação
     */
    naoRevalidarBoleta(id: number): Observable<any> {
        return this._http.post<any>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}/nao-revalidar`,
            {},
            { responseType: "text" as "json" }
        );
    }

    /**
     * Finaliza uma boleta de intermediação
     * @param id ID da boleta de intermediação
     * @returns Observable com o resultado da operação
     */
    finalizarBoleta(id: number): Observable<any> {
        return this._http.post<any>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}/finalizar`,
            {},
            { responseType: "text" as "json" }
        );
    }

    /**
     * Busca uma boleta de intermediação por ID
     * @param id ID da boleta de intermediação
     * @returns Observable com a boleta de intermediação encontrada
     */
    getLogEmailsBoletaById(id: number): Observable<ApiResponse<LogEmailBoletaPayload[]>> {
        return this._http.get<ApiResponse<LogEmailBoletaPayload[]>>(
            `${this.utilitesService.urlPrefix}${this.baseUrl}/${id}/log-envio-email`
        );
    }
}
